/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library for date and time formatting.
import PropTypes from 'prop-types' // Npm: prop-types library.
import * as XLSX from 'xlsx' // Npm: XLSX library.
import _ from 'underscore' // Npm: utility module.
import { toast } from 'react-hot-toast' // Npm: React hot toast for notifications.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries and mutations.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI DataGrid component.
import {
  Button,
  Flex,
  Box,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import OperatorPrefixDropDownOptions from 'components/OperatorPrefixDropDownOptions'
import FileUpload from 'components/FileUpload'
import OperatorPrefixUpsert from 'components/OperatorPrefixUpsert'
import OperatorPrefixDelete from 'components/OperatorPrefixDelete'
import { MemoizedInput } from 'components/MemoizedInput'

/*
 * GRAPHS
 */
import OperatorPrefixReadQuery from './__query__/index.operatorPrefix.read.query'
import OperatorPrefixCreateThroughExcelMutation from './__mutation__/index.operatorPrefix.createThroughExcel.mutation'

/*
 * STYLES
 */
import { buttonStyle } from './index.style'

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment - Pagination state for Material UI DataGrid.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })

  // Hook assignment - Column visibility model.
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })

  // Hook assignment - Search state.
  const [searchQuery, setSearchQuery] = React.useState('')

  // Hook assignment - File upload states.
  const [uploadingFile, setUploadingFile] = React.useState(false)
  const [onSuccessfulFileUpload, setOnSuccessfulFileUpload] = React.useState(false)

  // Hook assignment - Modal states.
  const { 'isOpen': isOperatorPrefixCreateOpen, 'onOpen': onOperatorPrefixCreateOpen, 'onClose': onOperatorPrefixCreateClose } = useDisclosure()
  const { 'isOpen': isOperatorPrefixUpdateOpen, 'onOpen': onOperatorPrefixUpdateOpen, 'onClose': onOperatorPrefixUpdateClose } = useDisclosure()
  const { 'isOpen': isOperatorPrefixDeleteOpen, 'onOpen': onOperatorPrefixDeleteOpen, 'onClose': onOperatorPrefixDeleteClose } = useDisclosure()

  // GraphQL Mutation - Create Operator Prefix through Excel file.
  const [MutationOperatorPrefixCreateThroughExcel] = useMutation(OperatorPrefixCreateThroughExcelMutation)

  // GraphQL Query - Fetch Operator Prefix data with pagination and search filters.
  const _QueryOperatorPrefixRead = useQuery(OperatorPrefixReadQuery, {
    'variables': {
      'mncId': passOn?.mncId,
      'search': searchQuery,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  // Ref assignment - Track first load completion and table header height.
  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)

  // Responsive view detection.
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Debounce handler - Optimize search input performance.
  const _onSearchInputChange = React.useRef(_.debounce((e) => setSearchQuery(e.target.value), 800)).current

  // Cleanup on unmount - Cancel debounced function to prevent memory leaks.
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment - Check if first load is completed.
  if (!_QueryOperatorPrefixRead.loading && 0 < _QueryOperatorPrefixRead.data?.OperatorPrefixRead?.length) {
    _isFirstLoadCompleted.current = true
  }

  if (0 === _QueryOperatorPrefixRead.data?.OperatorPrefixRead?.length ||
    (0 < _QueryOperatorPrefixRead.data?.OperatorPrefixRead?.length &&
      !_.every(_.pluck(_QueryOperatorPrefixRead.data?.OperatorPrefixRead, 'status'), i => _successFlags?.includes(i)))) {
    _isFirstLoadCompleted.current = false
  }

  // Columns definition - Define Material UI DataGrid columns.
  const columns = React.useMemo(() => [
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'operatorName',
      'headerName': 'Operator Name',
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'operatorPrefix',
      'headerName': 'Operator Prefix',
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'mnc',
      'headerName': 'Mnc',
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 120
    },
    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 180
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 180
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <OperatorPrefixDropDownOptions
          operatorPrefixId={row.id}
          onOperatorPrefixUpdate={onOperatorPrefixUpdateOpen}
          onOperatorPrefixDelete={onOperatorPrefixDeleteOpen}
        />
      )
    }
  ], [])

  // Rows mapping - Transform API data to DataGrid format.
  const rows = React.useMemo(() =>
    _.compact(
      _QueryOperatorPrefixRead.data?.OperatorPrefixRead?.map((item, index) =>
        _successFlags.includes(item?.status)
          ? {
            'id': item?.id || String.random(9),
            'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
            'operatorName': item?.Mnc?.network ?? '-',
            'operatorPrefix': item?.prefix ?? '-',
            'mnc': item?.Mnc?.mnc ?? '-',
            'createdAt': Moment(item?.createdAt).format('YYYY-MM-DD hh:mm:ss A'),
            'updatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD hh:mm:ss A')
          }
          : null
      )
    ) || [],
    [_QueryOperatorPrefixRead.data?.OperatorPrefixRead, paginationModel.page, paginationModel.pageSize]
  )

  // Return component.
  return (
    <>
      <Flex className='operatorPrefix base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '14px'}
          bg='white'
          minH={_isCurrentViewMobile ? '130vh' : '100vh'}
          p={_isCurrentViewMobile ? '12px' : '22px'}>

          {/* Header Section - Action buttons */}
          <Flex pb='0px' justify='space-between' overflow='hidden' flexWrap={{ 'base': 'wrap', 'md': 'wrap' }}>
            <Flex gap='12px'>
              <Tooltip label='Create New Operator Prefix' fontSize='sm'>
                <Button
                  onClick={onOperatorPrefixCreateOpen}
                  leftIcon={<IoMdAdd />}
                  style={buttonStyle}>
                  Create Prefix
                </Button>
              </Tooltip>
            </Flex>
            <Flex gap='12px'>
              <FileUpload
                uploadingFile={uploadingFile}
                bg={onSuccessfulFileUpload ? 'green.100' : void 0}
                color={onSuccessfulFileUpload ? 'green' : void 0}
                label={_isCurrentViewMobile ? '' : onSuccessfulFileUpload ? 'Upload Successful' : 'Upload Prefix'}
                onChange={e => Promise.all(Array.from(e.target.files).map(file => {
                  // Set uploading file to true.
                  setUploadingFile(true)

                  // File reader.
                  const _FileReader = new FileReader()

                  // Read file as binary string.
                  _FileReader.readAsBinaryString(file)

                  // Event handler.
                  _FileReader.onload = () => {
                    const _data = _FileReader.result
                    const _workbook = XLSX.read(_data, { 'type': 'binary' })
                    const _sheetName = _workbook.SheetNames[0]
                    const _sheet = _workbook.Sheets[_sheetName]
                    const _parsedData = XLSX.utils.sheet_to_json(_sheet)

                    // Execute mutation.
                    return MutationOperatorPrefixCreateThroughExcel({
                      'variables': {
                        'mncId': passOn?.mncId,
                        'file': _parsedData,
                        'name': file.name
                      }
                    }).then(e => {
                      // Set uploadingFile to false when upload completes
                      setUploadingFile(false)
                      setOnSuccessfulFileUpload(true)
                      const j = setTimeout(() => {
                        setOnSuccessfulFileUpload(false)
                        clearTimeout(j)
                      }, 3000)

                      // Toast the success message
                      e?.data?.OperatorPrefixCreateThroughExcel?.map(r => !_.isEmpty(r?.message) && toast(r?.message))
                    })
                  }

                  // Return void.
                  return void 0
                }))}
                accept='.xlsx, .xls, .csv'
                disabled={uploadingFile}
              />
            </Flex>
          </Flex>

          {/* Filter Section - Search input */}
          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='10px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='flex-start'
          >
            <Box minW='150px' w='200px'>
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Operator Prefix</Text>
              <MemoizedInput
                placeholder='Search by Prefix'
                className='filter searchInput'
                defaultValue={searchQuery}
                onChange={_onSearchInputChange}
              />
            </Box>
          </Flex>

          {/*  Main table  */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.first(_QueryOperatorPrefixRead?.data?.OperatorPrefixRead)?._totalCount ?? 0}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
                />
              ),
              'noResultsOverlay': () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
                />
              ),
              'noRowsOverlay': () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
                />
              )
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryOperatorPrefixRead?.loading}
          />
        </Flex>
        <Modal
          size='sm'
          title='Operator Prefix Create'
          isOpen={isOperatorPrefixCreateOpen}
          onClose={onOperatorPrefixCreateClose}>
          <OperatorPrefixUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='sm'
          title='Operator Prefix Update'
          isOpen={isOperatorPrefixUpdateOpen}
          onClose={onOperatorPrefixUpdateClose}>
          <OperatorPrefixUpsert />
        </Modal>
        <Modal
          size='md'
          title='Operator Prefix Delete'
          isOpen={isOperatorPrefixDeleteOpen}
          onClose={onOperatorPrefixDeleteClose}>
          <OperatorPrefixDelete />
        </Modal>
      </Flex>
    </>
  )
}

/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}

/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
